import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from 'styled-components';
import Input from './Input';
import Popup from './Popup';
import Checkbox from './Checkbox';
import Tooltip from './Tooltip';
import CoordinatesInput from './CoordinatesInput';
import CircleLoader from './CircleLoader';
import { InfoSmallIcon } from './utils/IconFactory/index';
import { isMobile } from './utils';
const isNumber = (v) => typeof v === 'number';
export default React.memo(function PlaceInput(props) {
    var _a, _b, _c;
    const timerRef = React.useRef(0);
    const inputTimerId = React.useRef(0);
    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const tr = (_a = props.t) !== null && _a !== void 0 ? _a : (v => v);
    const [value, setValue] = React.useState(props.value);
    const [places, setPlaces] = React.useState(null);
    const [lastPlaces, setLastPlaces] = React.useState(null);
    const [selected, setSelected] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [dropdownAbove, setDropdownAbove] = React.useState(false);
    const { id: placeId, lat, lon, originalLat, originalLon } = (props.place || {});
    const [showUserCoordinates, setShowUserCoordinates] = React.useState(!placeId && (lat !== originalLat || lon !== originalLon));
    const [customPlaceName, setCustomPlaceName] = React.useState(undefined);
    const reset = (value) => {
        setPlaces(null);
        setLastPlaces(null);
        setValue(value);
    };
    const [cachPlace, setCachPlace] = React.useState();
    React.useEffect(() => {
        reset(props.value);
        const place = props.place;
        if ((place === null || place === void 0 ? void 0 : place.originalLat) && (place === null || place === void 0 ? void 0 : place.originalLon)) {
            place.showUserCoordinates = showUserCoordinates;
            setCachPlace(place);
        }
    }, [props.value]);
    React.useEffect(() => {
        if (customPlaceName === undefined)
            return;
        const place = { ...props.place };
        place.id = null;
        place.name = customPlaceName;
        place.showUserCoordinates = showUserCoordinates;
        props.onSelect(place);
    }, [customPlaceName]);
    const onUserCoordinates = React.useCallback((lat, lon) => {
        var _a, _b;
        if ((lat === ((_a = props.place) === null || _a === void 0 ? void 0 : _a.lat)) && (lon === ((_b = props.place) === null || _b === void 0 ? void 0 : _b.lon)))
            return;
        clearTimeout(inputTimerId.current);
        inputTimerId.current = setTimeout(() => {
            const place = { ...props.place };
            place.id = null;
            place.lat = lat;
            place.lon = lon;
            place.showUserCoordinates = showUserCoordinates;
            props.onSelect(place);
        }, 0);
    }, [props.place, showUserCoordinates]);
    const onChangeShowCoordinates = React.useCallback((val) => {
        setShowUserCoordinates(val);
        const place = { ...props.place };
        const _cachedPlace = cachPlace;
        if (!val) {
            place.showUserCoordinates = false;
            props.onSelect(place);
            if (!_cachedPlace)
                return;
            if (_cachedPlace.originalLat != undefined && _cachedPlace.originalLon != undefined) {
                place.lat = _cachedPlace.originalLat;
                place.lon = _cachedPlace.originalLon;
                place.originalLat = _cachedPlace.originalLat;
                place.originalLon = _cachedPlace.originalLon;
                props.onSelect(place);
            }
            else if (_cachedPlace && _cachedPlace.id) {
                place.isLocality = true;
                place.id = _cachedPlace.id;
                onSelect(place);
            }
        }
        else {
            place.showUserCoordinates = true;
            props.onSelect(place);
        }
    }, [props.place, cachPlace]);
    React.useEffect(() => {
        const selectClick = (value) => {
            const el = containerRef.current.querySelectorAll('.place-input-item')[value];
            el.click();
        };
        const onKeyDown = (event) => {
            if (["ArrowUp", "ArrowDown", "Enter"].includes(event.code)) {
                if (places && places.length > 0 && containerRef.current) {
                    event.stopPropagation();
                    event.preventDefault();
                    switch (event.code) {
                        case "ArrowUp":
                            if (selected > 0)
                                setSelected(selected - 1);
                            break;
                        case "ArrowDown":
                            if (selected < (places === null || places === void 0 ? void 0 : places.length) - 1)
                                setSelected(selected + 1);
                            break;
                        case "Enter":
                            selectClick(selected);
                    }
                }
            }
        };
        if (containerRef.current) {
            const windowHeight = window.innerHeight;
            const { bottom: containerBottom } = containerRef.current.getBoundingClientRect();
            const spaceBottom = windowHeight - containerBottom;
            setDropdownAbove(isMobile() && spaceBottom < 150);
        }
        if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
            inputRef.current.removeEventListener('keydown', onKeyDown);
            inputRef.current.addEventListener('keydown', onKeyDown);
        }
        return () => {
            (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) && inputRef.current.removeEventListener('keydown', onKeyDown);
        };
    }, [inputRef, containerRef, selected, places]);
    React.useEffect(() => {
        if (containerRef.current) {
            const el = containerRef.current.querySelectorAll('.place-input-item')[selected];
            el && el.scrollIntoView();
        }
    }, [selected]);
    const fetchPlaces = async (value) => {
        var _a, _b;
        try {
            const result = await ((_a = props.asyncDataFn) === null || _a === void 0 ? void 0 : _a.call(props, value, props.lang));
            setLoading(false);
            (_b = props.onDataLoading) === null || _b === void 0 ? void 0 : _b.call(props, false);
            if (!(result === null || result === void 0 ? void 0 : result.length)) {
                setPlaces([]);
                setLastPlaces([]);
                return;
            }
            const _places = [
                ...result,
            ];
            if (props.matchPlace) {
                const matchedIndex = _places.findIndex((place) => {
                    var _a, _b;
                    return place[(_a = props.matchPlace) === null || _a === void 0 ? void 0 : _a.matchKey] === ((_b = props.matchPlace) === null || _b === void 0 ? void 0 : _b.matchValue);
                });
                if (matchedIndex != -1) {
                    _places.splice(0, 0, _places.splice(matchedIndex, 1)[0]);
                }
            }
            setPlaces(_places);
            setLastPlaces(_places);
            setSelected(0);
        }
        catch (err) {
            console.log('fetch places error -', err);
        }
    };
    const onChange = React.useCallback((value) => {
        var _a, _b, _c;
        const tValue = value.trim();
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = 0;
        if (tValue.length >= 3) {
            setLoading(true);
            (_a = props.onDataLoading) === null || _a === void 0 ? void 0 : _a.call(props, true);
            timerRef.current = window.setTimeout(() => fetchPlaces(tValue), 500);
        }
        else {
            setLoading(false);
            (_b = props.onDataLoading) === null || _b === void 0 ? void 0 : _b.call(props, false);
            setPlaces(null);
            setLastPlaces(null);
        }
        setValue(value);
        (_c = props.onChange) === null || _c === void 0 ? void 0 : _c.call(props, value);
        clearTimeout(inputTimerId.current);
        inputTimerId.current = setTimeout(() => {
            if (props.userCoordinates) {
                setCustomPlaceName(value);
            }
        }, 0);
    }, [props.place]);
    const onSelect = async (place) => {
        var _a;
        reset(place.name);
        if (place.isLocality) {
            const selectedPlace = await ((_a = props.asyncDetailFn) === null || _a === void 0 ? void 0 : _a.call(props, place.name, place.id, props.lang));
            selectedPlace.originalName = selectedPlace.name;
            selectedPlace.originalLat = selectedPlace.lat;
            selectedPlace.originalLon = selectedPlace.lon;
            setCustomPlaceName(undefined);
            props.onSelect(selectedPlace);
            setCachPlace(selectedPlace);
        }
        else {
            fetchPlaces(value);
        }
    };
    const onContainerClick = (evt) => {
        evt && evt.stopPropagation();
    };
    return (_jsxs(Container, { className: props.className, onClick: onContainerClick, ref: containerRef, children: [_jsxs("div", { className: 'place-input', children: [_jsx(Input, { ref: inputRef, size: props.size || 'medium', mode: props.mode || 'normal', value: value, onChange: onChange, placeholder: props.t ? (props.placeholder || tr("chronos.app.ui.PlaceInput.place")) : 'Населенный пункт, область, страна', autoFocus: props.autoFocus, onFocus: () => { var _a; setPlaces(lastPlaces); (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props); }, onBlur: props.onBlur, className: `${props.className || ''} ui-place-input`, disabled: props.disabled, rightIcon: props.rightIcon }), (places || loading) &&
                        _jsxs(Popup, { onClose: () => setPlaces(null), className: "place-input-dropdown", showAbove: dropdownAbove, scrollable: true, children: [loading && _jsx(Loader, { children: _jsx(CircleLoader, {}) }), places && places.length > 0
                                    ? _jsx(_Fragment, { children: places.map((place, i) => {
                                            const classes = ['place-input-item'];
                                            if (i === selected)
                                                classes.push('selected');
                                            let _nameSpan = _jsx("span", { children: place === null || place === void 0 ? void 0 : place.name });
                                            const _nameCoordsMatch = place.name.match(/(.+)(\(.*\))/);
                                            if (_nameCoordsMatch) {
                                                _nameSpan = _jsxs(_Fragment, { children: [_jsx("span", { className: 'name', children: _nameCoordsMatch[1] }), _jsx("span", { className: 'coords', children: _nameCoordsMatch[2] })] });
                                            }
                                            return _jsxs(Place, { onClick: (evt) => onSelect(place), className: classes.join(' '), children: [_nameSpan, (place && props.matchPlace && place[props.matchPlace.matchKey] === props.matchPlace.matchValue) &&
                                                        _jsx("span", { className: 'badge', children: props.matchPlace.matchMessage })] }, `place_${i}`);
                                        }) })
                                    : (!Boolean(places === null || places === void 0 ? void 0 : places.length) && !loading) &&
                                        _jsxs("p", { children: [_jsx("span", { children: props.t ? tr("chronos.app.ui.PlaceInput.didNotFindYourCity") : 'Не нашли свой город?' }), "\u00A0\u00A0", _jsx("a", { target: "_blank", href: "mailto:support@chronos.mg", children: "support@chronos.mg" })] })] })] }), props.notice && _jsx(Notice, { className: 'ch-field-notice', children: props.notice }), props.userCoordinates &&
                _jsxs("div", { className: 'user-coordinates', children: [_jsxs("div", { className: 'user-coordinates-control', children: [_jsx(Checkbox, { label: props.t ? tr("chronos.app.inputCoordinates") : "Ввести координаты", checked: showUserCoordinates, onChange: onChangeShowCoordinates }), _jsx(Tooltip, { text: props.t ? tr("chronos.app.specifyCoordinates") : `Уточните координаты места своего рождения.<br> Для вставки можно использовать комбинации клавиш Ctrl+C, Ctrl+V<br>Для восстановления оригинального места рождения воспользуйтесь поиском.`, textAlign: 'left', hPosition: 'left', children: _jsx(InfoSmallIcon, { width: "1.5rem", height: "1.5rem", style: { color: 'var(--ui-label-color)' } }) })] }), showUserCoordinates &&
                            _jsx(CoordinatesInput, { className: "user-coordinates-input", size: props.size, lat: (_b = props.place) === null || _b === void 0 ? void 0 : _b.lat, lon: (_c = props.place) === null || _c === void 0 ? void 0 : _c.lon, onCoordinates: onUserCoordinates, t: props.t })] })] }));
});
const Container = styled.div `
  width: 100%;
  position: relative;

  .place-input {
    position: relative;
  }

  p {
    position: relative;
    color: var(--ui-label-color);
    padding-left: 0.5em;
    font-size: 1em;

    a {
      color: var(--text-primary);
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .user-coordinates {
    position: relative;
    box-sizing: border-box;
    margin-top: 0.688rem;
  }

  .user-coordinates-control {
    display: flex;
    position: relative;
    justify-content: space-between;
    padding-inline: 0.125rem 0.062rem;
  }

  .user-coordinates-input {
    position: relative;
    margin-top: 1rem;
  }
`;
const Place = styled.div `
  display: flex;
  box-sizing: border-box;
  width: 100%;
  min-height: 2.25rem;
  margin-right: 0.313rem;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  text-align: left;
  align-items: center;
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: 0.25em;

  &:hover, &.selected {
    color: var(--text-primary);
    background-color: var(--bg-element-hover-hover);
  }

  .badge {
    margin-inline-start: auto;
    text-align: right;
    font-size: 0.75rem;

    color: var(--accent-color);
  }

`;
const Notice = styled.div `
  position: relative;
  margin-block-start: 0.375rem;
  font-size: 0.75rem;
  color: var(--accent-blue);
`;
const Loader = styled.div `
  position: relative;
  text-align: center;
  color: var(--text-primary);
  overflow: hidden;
`;
